import type { EventMessage, FeedLotState, FeedSaleState } from '@auctisync/engine-wire';
import type { AuctionBidHistoryEntry, LotBidHistoryEntry } from './bid-history.js';
import type { FeedLot, TickerEntry, YouStanding } from './engine-feed.js';
import { feedStateToLotStatus } from './live-lot.js';
import type { SoundLot } from './lot-sound.js';
import type { MyLotPosition, PublicLotRow } from './lots.js';

/**
 * The small rules a live PAGE needs between the feed and what it draws — for
 * the website and the app alike (2026-10-08, website build).
 *
 * `createSaleFeed` keeps the engine's lots; `toLiveLot` turns one into the row
 * a Lots-page layout renders. Pages also show lots in shapes that are NOT a
 * `LiveLot` — a search filter, the house's lot table, a watchlist card, a bid
 * history row, the activity ticker — and each of those had to answer the same
 * questions again: which figure wins, the engine's or the catalogue's? which
 * bid in the trail is mine? is this the same bid the database already showed
 * me? Answered here, once, so two pages (or two apps) cannot answer them
 * differently (memory: feedback_no_mechanical_divergence).
 *
 * Pure. No React, no I/O.
 */

// ─── The engine's figures over a catalogue row ─────────────────────────────

/** The catalogue fields the engine also carries — the ones a live page must not read stale. */
export interface LiveFigures {
  current_bid_cents: number | null;
  bid_count: number | null;
  ends_at: string | null;
  status: string;
}

/** A row `withLiveFigures` accepts: the figures, the close time optional (a house table may not read it). */
export type LiveFiguresRow = Omit<LiveFigures, 'ends_at'> & { ends_at?: string | null };

/**
 * A catalogue row with the engine's live figures laid over it — price, bid
 * count, close time and state — for every surface that reads a ROW rather
 * than a `LiveLot`: the search, filter and sort rules (`narrowRoom`,
 * `searchLots`), the house's lot tables, the watchlist and My bids.
 *
 * The rule is `toLiveLot`'s: the engine wins every field it carries, the row
 * supplies the rest. Without it a Lots page sorted "Most bids" or filtered
 * "under $50" by prices it was no longer showing.
 *
 * A null figure from the engine stays null — "hidden from you, or none", never
 * the catalogue's number for it: a house that hides its bid counts must not
 * have them filled back in from the database (memory:
 * reference_auction_visibility_flags). No live view (a Supabase-book sale, or
 * a lot the feed has not described yet) returns the row itself.
 */
export function withLiveFigures<R extends LiveFiguresRow>(
  row: R,
  live: FeedLot | null | undefined,
): Omit<R, keyof LiveFigures> & LiveFigures {
  if (live === null || live === undefined) {
    // The very row when it already has every figure (no copy, no re-render);
    // a row without a close time gains a null one.
    return row.ends_at !== undefined
      ? (row as Omit<R, keyof LiveFigures> & LiveFigures)
      : { ...row, ends_at: null };
  }
  return {
    ...row,
    current_bid_cents: live.current_high_cents,
    bid_count: live.bid_count,
    // The engine's clock moves with every extension; the row's is the plan.
    ends_at: live.close_time ?? row.ends_at ?? null,
    status: feedStateToLotStatus(live.state),
  };
}

// ─── A Supabase-book row, read by the sound rule ───────────────────────────

/**
 * The catalogue's words for a lot's state, in the engine's — the inverse of
 * `feedStateToLotStatus`. Both hammer outcomes the catalogue calls `unsold`
 * and `no_sale` are hammer states, which is all the sound rule asks.
 */
const STATE_OF: Record<string, FeedLotState> = {
  draft: 'pending',
  scheduled: 'pending',
  active: 'open',
  closing: 'closing',
  sold: 'closed_sold',
  unsold: 'closed_no_winner',
  no_sale: 'closed_no_sale',
  cancelled: 'canceled',
};

/**
 * A lot row from a sale on the Supabase book (Realtime), in the shape
 * `lotSoundFor` reads — so the parachute book plays its sounds by the same
 * rule as the engine: a bid on a lot on screen, a gavel when one closes.
 *
 * The row has no sequence and no last-bid time: `seq` is `'0'` and
 * `last_bid_at` is left out (not known), so the rule decides on the count and
 * the price alone. "Was it my bid?" cannot come from the engine here; pass
 * `you: undefined` and decide own bids by the caller's own record.
 */
export function soundLotFromRow(
  row: Pick<PublicLotRow, 'id' | 'status' | 'current_bid_cents'> & { bid_count: number | null },
): SoundLot {
  return {
    lot_id: row.id,
    state: Object.hasOwn(STATE_OF, row.status) ? (STATE_OF[row.status] as FeedLotState) : 'pending',
    bid_count: row.bid_count,
    current_high_cents: row.current_bid_cents,
    seq: '0',
  };
}

// ─── The sale's state before the engine says ───────────────────────────────

/**
 * `auctions.status` in the feed's words, for `createSaleFeed`'s
 * `initialSaleState` — which decides whether a long drop reads "Bidding
 * paused" before the engine has confirmed anything. Only a sale that is open
 * for bids is `live`; a sale in preview or not yet open is `scheduled` (a
 * drop then is not a pause), and anything unknown is null — never paused.
 * The engine's own `subscribed` replaces it the moment the socket is up.
 */
export function feedSaleStateFromAuctionStatus(
  status: string | null | undefined,
): FeedSaleState | null {
  switch (status) {
    case 'active':
      return 'live';
    case 'preview':
    case 'queued':
      return 'scheduled';
    case 'completed':
    case 'archived':
    case 'cancelled':
      return 'closed';
    default:
      return null;
  }
}

// ─── Which change was mine ─────────────────────────────────────────────────

/**
 * The sequence of a change that was the viewer's own doing, or null.
 *
 * The same rule `lotSoundFor` applies (`isOwnChange`): the engine sends the
 * bidder a `you` whenever their own standing moves, carrying the sequence of
 * the change that moved it; a move that took the lead AWAY from them
 * (`outbid`) was a rival's bid, not theirs. A page collects these to mark its
 * own bids in the history and the ticker — never a time window after pressing
 * Bid.
 */
export function ownSeqFromYou(you: YouStanding): string | null {
  return you.outbid ? null : you.seq;
}

// ─── Bid history and the ticker, from the engine ───────────────────────────

/** A bid-history row that came from the engine: the sequence travels with it. */
export interface LiveBidHistoryEntry extends LotBidHistoryEntry {
  lotId: string;
  /** The engine sequence of the bid — matched against `ownSeqFromYou`. */
  seq: string;
  /**
   * The engine said whose bid this is (`mine` was present): `isYou` is its
   * answer, and the sequence guess must not overrule it (`isOwnEntry`).
   */
  mineKnown: boolean;
}

/** An activity / ticker line that came from the engine. */
export interface LiveActivityEntry extends AuctionBidHistoryEntry {
  seq: string;
  /** As on `LiveBidHistoryEntry`. */
  mineKnown: boolean;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/**
 * One bid-history row from an engine `event`, or null when the event is not a
 * row of the trail.
 *
 * The trail is the `bid_accepted` events — the rows the database records
 * (20261007120000 projects each into `public.bids`; a proxy execution is not a
 * row, so it is not one here either). The fields are the ones
 * `get_lot_bid_history` returns: the amount POSTED (`amount_cents`, never the
 * ceiling — the engine strips that before the event leaves), when, and
 * whether the proxy placed it.
 *
 * The engine applies the house's privacy switches before sending: under
 * "hide winning info" another bidder's amount arrives null and stays null —
 * shown as a dash, never 0 — and under "hide bid history" another bidder's
 * events do not arrive at all.
 *
 * `isYou`: the engine's own answer where it gives one — `mine`, sent only to
 * a bidder's socket (2026-10-08; adopted 2026-10-11). Without it (a public
 * observer, an older engine) the viewer's own rows are still found by
 * sequence (`isOwnEntry`), from the bidder's `you`.
 */
export function historyEntryFromEvent(event: EventMessage): LiveBidHistoryEntry | null {
  if (event.event_type !== 'bid_accepted' || typeof event.lot_id !== 'string') return null;
  const p = event.payload;
  if (!isRecord(p)) return null;
  const amount = p.amount_cents;
  if (amount !== null && (typeof amount !== 'number' || !Number.isFinite(amount))) return null;
  if (typeof event.timestamp !== 'string' || !Number.isFinite(Date.parse(event.timestamp))) {
    return null;
  }
  return {
    id: event.event_id,
    lotId: event.lot_id,
    seq: event.sequence_num,
    amountCents: amount,
    createdAt: event.timestamp,
    isYou: event.mine === true,
    isProxy: p.is_proxy_placement === true,
    mineKnown: typeof event.mine === 'boolean',
  };
}

/**
 * One activity line from the engine's sale-wide `ticker`. The ticker carries
 * no bidder; on a bidder's socket it carries `mine` — whether the bid was
 * theirs (adopted 2026-10-11) — and without it the viewer's own lines are
 * found by sequence (`isOwnEntry`). `lotNumber` comes from the page's
 * catalogue — the engine knows lots by id.
 */
export function activityFromTicker(entry: TickerEntry, lotNumber: number): LiveActivityEntry {
  return {
    id: `ticker:${entry.seq}:${entry.lot_id}`,
    lotId: entry.lot_id,
    lotNumber,
    seq: entry.seq,
    amountCents: entry.amount_cents,
    createdAt: entry.at,
    isYou: entry.mine === true,
    isProxy: false,
    mineKnown: typeof entry.mine === 'boolean',
  };
}

/**
 * True when the row is the viewer's: the database or the engine said so, or —
 * only where neither said either way — a `you` carried its sequence.
 *
 * 🔴 THE ENGINE'S `mine` WINS (2026-10-11). `you.seq` is the LAST event of a
 * change; a bid the leader's proxy answers at once is not its change's last
 * event, so the sequence guess can miss the bidder's own bid. Where the engine
 * named the owner (`mineKnown`), its answer stands, yes or no.
 */
export function isOwnEntry(
  entry: Pick<LotBidHistoryEntry, 'isYou'> & { seq?: string; mineKnown?: boolean },
  ownSeqs: ReadonlySet<string>,
): boolean {
  if (entry.isYou) return true;
  if (entry.mineKnown === true) return false;
  return entry.seq !== undefined && ownSeqs.has(entry.seq);
}

/**
 * The bid list a page shows: the database's backlog (read once, on open and
 * after a reconnect) and the engine's live rows, merged — newest first, each
 * bid once, at most `limit`.
 *
 * The SAME bid can arrive both ways while a page opens: the engine's row
 * live, and the database's copy of it a moment later. They share no id (the
 * database keys its own), but the database records the engine's own clock
 * for the bid (`created_at` = the event's timestamp, 20261007120000), so a
 * bid is identified by its lot and its instant to the millisecond. Where the
 * two copies differ, the viewer's own mark wins — the database decides
 * `isYou` server-side.
 */
export function mergeBidEntries<
  T extends { id: string; createdAt: string; isYou: boolean; lotId?: string },
>(lists: readonly (readonly T[])[], limit: number): T[] {
  const byKey = new Map<string, T>();
  const ids = new Set<string>();
  for (const list of lists) {
    for (const entry of list) {
      if (ids.has(entry.id)) continue;
      ids.add(entry.id);
      const ms = Date.parse(entry.createdAt);
      const key = `${entry.lotId ?? ''}|${Number.isFinite(ms) ? String(ms) : entry.id}`;
      const held = byKey.get(key);
      if (held === undefined) byKey.set(key, entry);
      else if (!held.isYou && entry.isYou) byKey.set(key, entry);
    }
  }
  return [...byKey.values()]
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, Math.max(0, limit));
}

// ─── What the bidder holds, live ───────────────────────────────────────────

/**
 * The bidder's position on a lot with the engine's account of their ceiling
 * laid over the database's. The engine holds the ceiling that is bidding for
 * them; the database copy can trail it (the write-behind), and a stale
 * ceiling is how `explainPointlessBid` would tell a bidder their new maximum
 * is "already in" when it is not. `highestBidCents` (the floor posted) is the
 * database's alone.
 */
export function positionWithYou(
  position: MyLotPosition,
  you: YouStanding | null | undefined,
): MyLotPosition {
  if (you === null || you === undefined || you.ceiling_cents === null) return position;
  return { ...position, hasBid: true, ceilingCents: you.ceiling_cents };
}
